// Reporte mensual ejecutivo en Excel — el entregable para dirección:
// "llegaron X por canal, avanzaron Y, se inscribieron Z" con comparativa
// contra el mes anterior. Usa la atribución certificada (source_metadata)
// y la fecha real de inscripción (no el created_at del lead).
import * as XLSX from "xlsx"
import { normalizeStatus } from "@/lib/supabase"
import { getTopChannel, getMetaNetwork, getCampaignName } from "@/lib/attribution"
import { getNivelLabel } from "@/lib/educational-levels"
import { getSourceMeta } from "@/components/kanban/lead-source-badge"

const ADVANCED = new Set(["cita", "interesado", "entrega_resultado", "pendiente_pago", "inscrito"])
const CHANNEL_ORDER = ["whatsapp_directo", "landing", "meta_ads", "recomendacion", "manual"]

const ETAPA_LABELS = {
  nuevo: "Primer contacto",
  contactado: "Proceso",
  cita_solicitada: "Solicita cita",
  cita: "Cita agendada",
  interesado: "Examen",
  entrega_resultado: "Entrega de resultado",
  pendiente_pago: "Pendiente de pago",
  inscrito: "Inscrito",
  perdido: "Perdido",
  inactivo: "Inactivo",
  spam: "Spam",
}

const getInscripcionDate = (l) => {
  const raw = l.inscripcion_confirmada_at || l.fecha_inscripcion || l.updated_at || l.created_at
  const d = raw ? new Date(raw) : null
  return d && !isNaN(d.getTime()) ? d : null
}

const monthRange = (base, offset = 0) => {
  const start = new Date(base.getFullYear(), base.getMonth() + offset, 1)
  const end = new Date(base.getFullYear(), base.getMonth() + offset + 1, 1)
  return [start, end]
}

const within = (d, [start, end]) => d && d >= start && d < end
const createdWithin = (l, range) => {
  const d = l.created_at ? new Date(l.created_at) : null
  return within(d && !isNaN(d.getTime()) ? d : null, range)
}

const pct = (num, den) => (den > 0 ? `${((num / den) * 100).toFixed(1)}%` : "—")
const delta = (actual, anterior) => {
  if (anterior === 0 && actual === 0) return "="
  if (anterior === 0) return `+${actual}`
  const d = actual - anterior
  return `${d >= 0 ? "+" : ""}${d} (${((d / anterior) * 100).toFixed(0)}%)`
}

export function generateMonthlyReport(allLeads = [], selectedMonth = new Date()) {
  const mes = monthRange(selectedMonth, 0)
  const mesAnterior = monthRange(selectedMonth, -1)
  const mesLabel = selectedMonth.toLocaleDateString("es-MX", { month: "long", year: "numeric" })
  const prevLabel = mesAnterior[0].toLocaleDateString("es-MX", { month: "long" })

  const cohorte = allLeads.filter((l) => createdWithin(l, mes))
  const cohortePrev = allLeads.filter((l) => createdWithin(l, mesAnterior))
  const esInscrito = (l) => normalizeStatus(l.estado_lead) === "inscrito"
  const inscritosMes = allLeads.filter((l) => esInscrito(l) && within(getInscripcionDate(l), mes))
  const inscritosPrev = allLeads.filter((l) => esInscrito(l) && within(getInscripcionDate(l), mesAnterior))

  const porCanal = (lista) => {
    const out = {}
    for (const l of lista) {
      const ch = getTopChannel(l)
      out[ch] = (out[ch] || 0) + 1
    }
    return out
  }
  const canalMes = porCanal(cohorte)
  const canalPrev = porCanal(cohortePrev)
  const avanzaron = cohorte.filter((l) => ADVANCED.has(normalizeStatus(l.estado_lead))).length
  const avanzaronPrev = cohortePrev.filter((l) => ADVANCED.has(normalizeStatus(l.estado_lead))).length

  // ── Hoja 1: Resumen ejecutivo ──
  const resumen = [
    { Concepto: `RESUMEN — ${mesLabel.toUpperCase()}`, [mesLabel]: "", [`vs ${prevLabel}`]: "" },
    { Concepto: "Prospectos nuevos", [mesLabel]: cohorte.length, [`vs ${prevLabel}`]: delta(cohorte.length, cohortePrev.length) },
    ...CHANNEL_ORDER.filter((ch) => (canalMes[ch] || 0) + (canalPrev[ch] || 0) > 0).map((ch) => ({
      Concepto: `   · ${getSourceMeta(ch).label}`,
      [mesLabel]: canalMes[ch] || 0,
      [`vs ${prevLabel}`]: delta(canalMes[ch] || 0, canalPrev[ch] || 0),
    })),
    { Concepto: "Llegaron a cita o más (de los nuevos)", [mesLabel]: `${avanzaron} (${pct(avanzaron, cohorte.length)})`, [`vs ${prevLabel}`]: delta(avanzaron, avanzaronPrev) },
    { Concepto: "Inscritos en el mes (por fecha de inscripción)", [mesLabel]: inscritosMes.length, [`vs ${prevLabel}`]: delta(inscritosMes.length, inscritosPrev.length) },
    { Concepto: "Conversión de la cohorte (inscritos / nuevos)", [mesLabel]: pct(cohorte.filter(esInscrito).length, cohorte.length), [`vs ${prevLabel}`]: "" },
  ]

  // ── Hoja 2: Campañas y orígenes ──
  const grupos = new Map()
  for (const l of cohorte) {
    const ch = getTopChannel(l)
    if (ch === "sin_fuente") continue
    let nombre, red = ""
    if (ch === "meta_ads") {
      nombre = getCampaignName(l)
      red = getMetaNetwork(l) || "sin red"
    } else {
      nombre = getSourceMeta(ch).label
    }
    const key = `${ch}|${nombre}`
    if (!grupos.has(key)) grupos.set(key, { canal: getSourceMeta(ch).label, nombre, redes: new Set(), total: 0, avanzaron: 0, inscritos: 0 })
    const g = grupos.get(key)
    g.total++
    if (red) g.redes.add(red)
    const st = normalizeStatus(l.estado_lead)
    if (ADVANCED.has(st)) g.avanzaron++
    if (st === "inscrito") g.inscritos++
  }
  const campanas = Array.from(grupos.values())
    .sort((a, b) => b.total - a.total)
    .map((g) => ({
      Canal: g.canal,
      "Campaña / Origen": g.nombre,
      Redes: Array.from(g.redes).join(", "),
      Leads: g.total,
      "A cita o más": g.avanzaron,
      Inscritos: g.inscritos,
      "Tasa avance": pct(g.avanzaron, g.total),
    }))

  // ── Hoja 3: Inscritos del mes ──
  const inscritos = inscritosMes
    .sort((a, b) => getInscripcionDate(a) - getInscripcionDate(b))
    .map((l) => ({
      Tutor: l.nombre_tutor || "Sin nombre",
      Alumno: l.nombre_alumno || "",
      Nivel: getNivelLabel(l.nivel_educativo) || "",
      CAP: l.asesor_nombre || "",
      Origen: getSourceMeta(getTopChannel(l)).label,
      Campaña: getTopChannel(l) === "meta_ads" ? getCampaignName(l) : "",
      "Fecha inscripción": getInscripcionDate(l)?.toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }) || "",
      Teléfono: l.telefono || "",
    }))

  // ── Hoja 4: Etapa actual de la cohorte del mes ──
  const etapas = {}
  for (const l of cohorte) {
    const st = normalizeStatus(l.estado_lead) || "nuevo"
    etapas[st] = (etapas[st] || 0) + 1
  }
  const etapasRows = Object.entries(etapas)
    .sort((a, b) => b[1] - a[1])
    .map(([st, n]) => ({ Etapa: ETAPA_LABELS[st] || st, Leads: n, "% de la cohorte": pct(n, cohorte.length) }))

  // ── Armar y descargar ──
  const wb = XLSX.utils.book_new()
  const wsResumen = XLSX.utils.json_to_sheet(resumen)
  wsResumen["!cols"] = [{ wch: 44 }, { wch: 18 }, { wch: 16 }]
  XLSX.utils.book_append_sheet(wb, wsResumen, "Resumen")
  const wsCamp = XLSX.utils.json_to_sheet(campanas.length ? campanas : [{ Canal: "Sin datos" }])
  wsCamp["!cols"] = [{ wch: 16 }, { wch: 34 }, { wch: 16 }, { wch: 7 }, { wch: 12 }, { wch: 9 }, { wch: 11 }]
  XLSX.utils.book_append_sheet(wb, wsCamp, "Campañas")
  const wsIns = XLSX.utils.json_to_sheet(inscritos.length ? inscritos : [{ Tutor: "Sin inscritos este mes" }])
  wsIns["!cols"] = [{ wch: 26 }, { wch: 22 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 24 }, { wch: 16 }, { wch: 16 }]
  XLSX.utils.book_append_sheet(wb, wsIns, "Inscritos del mes")
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(etapasRows.length ? etapasRows : [{ Etapa: "Sin datos" }]), "Etapas")

  const slug = `${selectedMonth.getFullYear()}-${String(selectedMonth.getMonth() + 1).padStart(2, "0")}`
  XLSX.writeFile(wb, `reporte_mensual_tepeyac_${slug}.xlsx`)
  return { cohorte: cohorte.length, inscritos: inscritosMes.length }
}
